/**
 * toast — sealed sonner boundary (W2.7).
 * All app code imports from here — never from 'sonner' directly.
 * Coerces mistaken shadcn-style toast({ title, description }) and try/catches every call.
 */
import { toast as sonnerToast } from 'sonner';

const REACT_ELEMENT = Symbol.for('react.element');

function isReactNode(value) {
  return (
    value != null &&
    typeof value === 'object' &&
    value.$$typeof === REACT_ELEMENT
  );
}

export function coerceMessage(value, fallback = 'Notice') {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object' && value.title != null) return String(value.title);
  return fallback;
}

/**
 * @param {any} [options]
 */
function normalizeOptions(options = {}) {
  /** @type {any} */
  const out = { ...options };
  if (out.description != null) out.description = String(out.description);
  delete out.variant;
  return out;
}

function splitShadcnStyleArg(message, options = {}) {
  if (typeof message !== 'object' || message === null || Array.isArray(message) || isReactNode(message)) {
    return null;
  }
  if (message.title == null && message.description == null && message.variant == null) {
    return null;
  }
  const title = coerceMessage(message.title ?? message, 'Notice');
  const description = message.description ?? options.description;
  const variant = message.variant ?? options.variant;
  return { title, description, variant, rest: { ...options, description, variant } };
}

function safeDispatch(fn) {
  try {
    return fn();
  } catch (err) {
    console.warn('[toast] failed safely:', err?.message ?? err);
    return undefined;
  }
}

function resolveCall(message, options = {}) {
  const shadcn = splitShadcnStyleArg(message, options);
  if (shadcn) {
    return {
      message: shadcn.title,
      options: normalizeOptions(shadcn.rest),
      variant: shadcn.variant,
    };
  }
  if (isReactNode(message)) {
    return { message, options: normalizeOptions(options), isNode: true };
  }
  return {
    message: coerceMessage(message),
    options: normalizeOptions(options),
    variant: options.variant,
  };
}

function invokeDefault(message, options = {}) {
  return safeDispatch(() => {
    const resolved = resolveCall(message, options);
    if (resolved.isNode) return sonnerToast(resolved.message, resolved.options);
    if (resolved.variant === 'destructive') return sonnerToast.error(resolved.message, resolved.options);
    return sonnerToast.success(resolved.message, resolved.options);
  });
}

function wrapMethod(methodName) {
  return (message, options) =>
    safeDispatch(() => {
      const resolved = resolveCall(message, options ?? {});
      const method = sonnerToast[methodName];
      if (typeof method !== 'function') {
        return sonnerToast(resolved.message, resolved.options);
      }
      return method.call(sonnerToast, resolved.message, resolved.options);
    });
}

function toast(message, options) {
  return invokeDefault(message, options);
}

toast.success = wrapMethod('success');
toast.error = wrapMethod('error');
toast.info = wrapMethod('info');
toast.warning = wrapMethod('warning');
toast.loading = wrapMethod('loading');
toast.message = invokeDefault;
toast.dismiss = (...args) => safeDispatch(() => /** @type {any} */ (sonnerToast).dismiss?.(...args));
toast.promise = (...args) => safeDispatch(() => /** @type {any} */ (sonnerToast).promise(...args));
toast.custom = (...args) => safeDispatch(() => /** @type {any} */ (sonnerToast).custom(...args));

/** @deprecated use toast() — kept for one release of call-site migration */
export function safeToast(message, options) {
  return toast(message, options);
}

export { toast };
export default toast;
